import { faClapperboardPlay } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AssistChip, Stack, Text } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { ITableRowProps } from '~/components/molecules/Table';
import type { IApiGetLiveStreamsItemResponseBody } from '~/lib/api/modules/LiveStreams';
import { RelativeTime } from '~/components/atoms/RelativeTime';
import { Table } from '~/components/molecules/Table';
import { HlsVideoPlayer } from '../HlsVideoPlayer';
import { LiveStreamListItemMenu } from './LiveStreamListItemMenu';

const MAX_RECORDINGS = 3;
const RECORDING_WIDTH = 120;
const RECORDING_HEIGHT = Math.round(RECORDING_WIDTH / 1.8);

export type ILiveStreamListItemProps = ITableRowProps & {
  liveStream: IApiGetLiveStreamsItemResponseBody;
};

const styles = stylex.create({
  recordings: {
    display: 'grid',
    gridTemplateColumns: `repeat(${MAX_RECORDINGS + 1}, min-content)`,
    gap: spacingTokens.padding$3,
  },
  recordingPlaceholder: {
    borderRadius: shapeTokens.corner$xs,
    backgroundColor: colorSchemeTokens.primaryContainer,
    color: colorSchemeTokens.onPrimaryContainer,
    width: RECORDING_WIDTH,
    height: RECORDING_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export const LiveStreamListItem: React.FC<ILiveStreamListItemProps> = (
  props,
) => {
  const { liveStream, ...other } = props;
  const { t } = useTranslation();

  const ended = !!liveStream.expired;
  const started =
    !ended &&
    liveStream.startDateTime &&
    DateTime.fromISO(liveStream.startDateTime) <= DateTime.now();
  const programmed = !ended && !started;

  return (
    <Table.Row {...other}>
      <Stack
        as={Table.BodyAction}
        justify="center"
        gap={2}
        href={`/apps/${liveStream.appId}/liveStreams/${liveStream._id}`}
      >
        <Stack gap={1}>
          {liveStream.displayName && (
            <Text variant="title" size="sm">
              {liveStream.displayName}
            </Text>
          )}

          <Stack horizontal gap={2}>
            <Stack horizontal gap={1}>
              <FontAwesomeIcon icon={faClapperboardPlay} />
              <Text truncateLines={1}>
                {liveStream.recordings?.length ?? 0}
              </Text>
            </Stack>
          </Stack>
        </Stack>

        {liveStream.recordings?.length && (
          <div {...stylex.props(styles.recordings)}>
            {liveStream.recordings
              ?.slice()
              .reverse()
              .slice(0, MAX_RECORDINGS)
              .map(
                (recording, recordingIndex) =>
                  recording.root && (
                    <HlsVideoPlayer
                      key={recordingIndex}
                      src={`${recording.baseUrl}/${recording.root}/${recording.playlist}`}
                      width={RECORDING_WIDTH}
                      height={RECORDING_HEIGHT}
                    />
                  ),
              )}

            {MAX_RECORDINGS !== undefined &&
              liveStream.recordings?.length > MAX_RECORDINGS && (
                <div {...stylex.props(styles.recordingPlaceholder)}>
                  <Text variant="label" size="md">
                    {t('LiveStreamListItem.oldRecordingsCount', {
                      count: liveStream.recordings.length - MAX_RECORDINGS,
                    })}
                  </Text>
                </div>
              )}
          </div>
        )}
      </Stack>

      <Stack as={Table.Body} gap={1}>
        <Stack horizontal gap={2}>
          <AssistChip
            label={
              ended
                ? t('LiveStreamListItem.expired')
                : programmed
                  ? t('LiveStreamListItem.programmed')
                  : t('LiveStreamListItem.ready')
            }
          />
        </Stack>
        <Text as="div" variant="body" size="sm" dimmed>
          <RelativeTime
            date={DateTime.fromISO(
              (started
                ? liveStream.expireDateTime
                : liveStream.startDateTime) ?? '',
            )}
            i18nKey={started ? 'LiveStreamListItem.expiresIn' : undefined}
          />
        </Text>
      </Stack>

      <Stack as={Table.Body} horizontal justify="end">
        <LiveStreamListItemMenu liveStream={liveStream} />
      </Stack>
    </Table.Row>
  );
};
